import type { ReactNode } from 'react';
import { Icon } from '../icons/Icon';

interface EmptyStateProps {
  title: string;
  description?: string;
  /** The empty state always proposes the next move (rule 19). */
  action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="empty">
      <svg viewBox="0 0 64 64" width="52" height="52" fill="none" aria-hidden="true">
        <rect
          x="11"
          y="11"
          width="42"
          height="42"
          rx="11"
          stroke="var(--line-3)"
          strokeWidth="4"
          strokeDasharray="6 5"
        />
        <circle cx="32" cy="32" r="7" fill="var(--line-2)" />
        <path
          d="M32 3.5V11M32 53v7.5M3.5 32H11M53 32h7.5"
          stroke="var(--line-3)"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
      <h3>{title}</h3>
      {description === undefined ? null : <p className="note">{description}</p>}
      {action}
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  description?: string;
  /** Recovery action (retry, go back): an error state never leaves the user stuck. */
  action?: ReactNode;
}

export function ErrorState({ title, description, action }: ErrorStateProps) {
  return (
    <div className="empty" role="alert">
      <Icon name="alertCircle" size={36} style={{ color: 'var(--crit)' }} />
      <h3>{title}</h3>
      {description === undefined ? null : <p className="note">{description}</p>}
      {action}
    </div>
  );
}

interface SkeletonProps {
  lines?: number;
  height?: number;
}

/** Skeleton for structure only, never for operational values (rule 18). */
export function Skeleton({ lines = 3, height = 12 }: SkeletonProps) {
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skel" style={{ height, width: `${String(100 - (i % 3) * 18)}%` }} />
      ))}
    </div>
  );
}
